import React, { memo, useCallback, useState } from 'react';
import { View, useTheme } from 'tamagui';
import { formatRelativeTime } from '../../../utils/dateTime';
import { CobuText, CobuView, CobuStack } from '@cobu/ui';
import AutoLinkText from './AutoLinkText';
import { GroupComment, GroupMember } from '@cobu/types';
import { Strings, URLS } from '../../../constants';
import CommentImageLayout from '../../../components/chat/groupDiscussion/CommentImageLayout';
import { useCommentReplies } from '../../../hooks/useCommentReplies';
import { CobuSimpleAvatar } from '@cobu/ui';
import CommentActions from './CommentActions';
import EnhancedReactionSelector from './EnhancedRectionSelector';
import ReactionSummary from './ReactionSummary';

interface CommentRepliesProps {
  parentComment: GroupComment;
  replies: GroupComment[];
  currentUserId: string;
  groupMembers: GroupMember[];
  highlightReplyId?: string;
  forceExpandReplies?: boolean;
  onReply?: (comment: GroupComment) => void;
  onReact?: (commentId: string, reaction: string | null) => void;
  onEdit?: (comment: GroupComment) => void;
  onDelete?: (comment: GroupComment) => void;
  onReport?: (userId: string) => void;
  onUserProfile?: (userId: string) => void;
  allowReactions?: boolean;
  allowReplies?: boolean;
  allowEditing?: boolean;
  allowReporting?: boolean;
  onShowReactionModal?: (commentId: string) => void;
  onFlagComment?: (commentId: string) => void;
  onBlockUser?: (userId: string) => void;
}

const CommentReplies: React.FC<CommentRepliesProps> = memo(({ ...props }) => {
  const {
    parentComment,
    replies,
    currentUserId,
    groupMembers,
    highlightReplyId,
    forceExpandReplies = false,
    onReply,
    onReact,
    onEdit,
    onDelete,
    onReport,
    onUserProfile,
    allowReactions = true,
    allowReplies = true,
    allowEditing = true,
    allowReporting = true,
    onShowReactionModal,
    onFlagComment,
    onBlockUser,
  } = props;

  const strings = Strings.get().discussion;
  const theme = useTheme();

  // Dynamic row height map for exact long-line height
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
  const handleRowLayout = useCallback((id: string) => (e: any) => {
    const h = Math.round(e.nativeEvent.layout.height);
    setRowHeights(prev => (prev[id] === h ? prev : { ...prev, [id]: h }));
  }, []);

  // Capture avatar center X per reply for pixel-perfect line alignment
  const [avatarCenters, setAvatarCenters] = useState<Record<string, number>>({});
  const handleAvatarLayout = useCallback((id: string) => (e: any) => {
    const { x, width } = e.nativeEvent.layout;
    const center = Math.round(x + width / 2);
    setAvatarCenters(prev => (prev[id] === center ? prev : { ...prev, [id]: center }));
  }, []);

  const {
    visibleReplies,
    repliesToShow,
    hasMultipleReplies,
    expanded,
    handleToggleExpanded,
    handleReply,
    handleReact,
  } = useCommentReplies({
    replies,
    currentUserId,
    parentComment,
    onReply,
    onReact,
    forceExpandReplies,
  });

  const validateUserProfile = (reply: GroupComment) => {
    return reply?.user?.deactivated;
  };

  const handleUserProfile = useCallback((reply: GroupComment) => {
    if (!onUserProfile) return;
    if (validateUserProfile(reply)) return;
    onUserProfile(reply.user.uuid);
  }, [onUserProfile, validateUserProfile]);

  if (visibleReplies.length === 0) return null;

  const formatReplyTime = useCallback((createdAt: string) => {
    return formatRelativeTime(createdAt);
  }, []);

  const renderReply = useCallback((reply: GroupComment, index: number) => {
    const isLastReply = index === visibleReplies.length - 1;
    const isLastShownReply = index === repliesToShow.length - 1;
    const isCurrentUser = reply.user.uuid === currentUserId;
    const replyTime = formatReplyTime(reply.createdAt);
    const isHighlighted = Boolean(highlightReplyId && reply.uuid === highlightReplyId);

    const hasImages = !!(reply.images && reply.images.length > 0);
    const isShortLine = isLastReply || !expanded;
    const longHeight = rowHeights[reply.uuid];
    const avatarCenter = avatarCenters[reply.uuid];

    return (
      <View key={reply.uuid} style={{ position: 'relative' }} onLayout={handleRowLayout(reply.uuid)}>
        {/* Absolute vertical line at measured avatar center */}
        <View
          style={[
            {
              position: 'absolute',
              left: (avatarCenter ?? 78) - 1.5, // fallback to legacy 78px, subtract half line width
              top: 0,
              width: 0,
              borderLeftWidth: 3,
              borderLeftColor: theme.black?.val,
              zIndex: 0,
            },
            isShortLine
              ? { height: hasImages ? 150 : 75 }
              : { height: longHeight ? longHeight : '100%' },
          ]}
        />

        <CobuStack layout="horizontal" ai="flex-start" gap="$2">
          {/* Avatar */}
          <CobuView onPress={() => handleUserProfile(reply)} style={{ marginRight: 5, marginTop: 14, marginBottom: 14, zIndex: 2 }} >
            <View onLayout={handleAvatarLayout(reply.uuid)}>
              {reply.user.profileImage && reply.user.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
                <View style={{ overflow: 'hidden', borderRadius: 15, width: 30, height: 30 }}>
                  <img
                    src={reply.user.profileImage}
                    height={30}
                    width={30}
                    style={{ objectFit: 'cover', display: 'block' }}
                    onError={(e) => (e.currentTarget.src = '/assets/images/mask.png')}
                    alt={Strings.get().ui.userAvatar.profileAlt}
                  />
                </View>
              ) : (
                <CobuSimpleAvatar user={reply.user} size={30} fontSize={14} />
              )}
            </View>
          </CobuView>

          {/* Reply Content */}
          <CobuStack
            f={1}
            mt={14}
            gap="$2"
            p={isHighlighted ? "$2" : undefined}
            br="$3"
            bw={isHighlighted ? 2 : 0}
            boc={isHighlighted ? "$cobuTeal" : undefined}
          >
            {/* Header with user name, timestamp, and actions */}
            <CobuStack layout="horizontal" ai="center" jc="space-between">
              <CobuStack layout="horizontal" ai="center" gap="$2" f={1}>
                <CobuView onPress={() => handleUserProfile(reply)}>
                  <CobuText variant="body">
                    {reply.user.displayName}
                  </CobuText>
                </CobuView>
                <CobuText variant="caption" color="$colorSecondary" fos="$2">
                  {replyTime}
                </CobuText>
              </CobuStack>

              {!(reply.removed) && (
                <CommentActions
                  comment={reply}
                  isCurrentUser={isCurrentUser}
                  isPinned={false}
                  allowEditing={allowEditing}
                  allowReporting={allowReporting}
                  onEdit={onEdit}
                  onDelete={() => onDelete?.(reply)}
                  onReport={onReport}
                  onFlag={onFlagComment ? () => onFlagComment(reply.uuid) : undefined}
                  onBlock={onBlockUser ? () => onBlockUser(reply.user.uuid) : undefined}
                />
              )}
            </CobuStack>

            {reply.removed && reply.removedBy === 'USER' ? (
              <CobuText variant="body">
                {strings.moderation.contentRemoved}
              </CobuText>
            ) : (
              <AutoLinkText
                text={reply.contents || ''}
                variant="body"
                fontSize="$4"
                color="$color"
              />
            )}

            {!reply.removed && hasImages && (
              <View mt="$2">
                <CommentImageLayout
                  images={reply.images || []}
                  isParentComment={false}
                  isAuthor={isCurrentUser}
                />
              </View>
            )}

            {!reply.removed && (
              <CobuStack gap="$2">
                {/* Reaction summary for reply */}
                {allowReactions && (
                  <ReactionSummary
                    comment={reply}
                    currentUserId={currentUserId}
                    onReactToComment={onReact ? async (commentId, reaction) => {
                      onReact(commentId, reaction);
                      return Promise.resolve();
                    } : undefined}
                    onShowReactionModal={onShowReactionModal}
                  />
                )}

                {/* Actions row */}
                <CobuStack layout="horizontal" ai="center" gap="$4" mt="$1">
                  {allowReactions && (
                    <EnhancedReactionSelector
                      comment={reply}
                      currentUserId={currentUserId}
                      isParent={false}
                      onReactToComment={onReact ? async (commentId: string, reaction: string | null) => {
                        onReact(commentId, reaction);
                        return Promise.resolve();
                      } : undefined}
                    />
                  )}
                  {allowReplies && isLastShownReply && (
                    <CobuView onPress={() => handleReply(reply)}>
                      <CobuStack layout="horizontal" ai="center" gap="$1">
                        <CobuText variant="caption" color="$textLinkColor">
                          {strings.buttons.reply}
                        </CobuText>
                      </CobuStack>
                    </CobuView>
                  )}
                </CobuStack>
              </CobuStack>
            )}
          </CobuStack>
        </CobuStack>
      </View>
    );
  }, [
    visibleReplies.length,
    repliesToShow.length,
    currentUserId,
    onUserProfile,
    handleUserProfile,
    strings.moderation.contentRemoved,
    allowReactions,
    allowReplies,
    allowEditing,
    allowReporting,
    onEdit,
    onDelete,
    onReport,
    onFlagComment,
    onBlockUser,
    handleReact,
    handleReply,
    formatReplyTime,
    expanded,
    rowHeights,
    avatarCenters,
    onShowReactionModal,
    highlightReplyId,
  ]);

  return (
    <View style={{ flexDirection: 'row' }}>
      <View style={{ flex: 1, marginRight: 5 }} />
      <View style={{ flex: 5 }}>
        {hasMultipleReplies && !expanded && (
          <CobuView onPress={handleToggleExpanded}>
            <CobuStack layout="horizontal" ai="center" gap="$1" ml={60} mb="$2">
              {/* <MaterialIcons name="expand-more" size={16} color={theme.colorSecondary.val} /> */}
              <CobuText variant="caption">
                {strings.buttons.seeMoreReplies.replace('{count}', (visibleReplies.length - 1).toString())}
              </CobuText>
            </CobuStack>
          </CobuView>
        )}

        {/* Remove inter-row gap to keep the line continuous */}
        <CobuStack>{repliesToShow.map((reply, index) => renderReply(reply, index))}</CobuStack>

        {expanded && hasMultipleReplies && (
          <CobuView onPress={handleToggleExpanded}>
            <CobuStack layout="horizontal" ai="center" gap="$1" ml={60} mt="$2">
              {/* <MaterialIcons name="expand-less" size={16} color={theme.colorSecondary.val} /> */}
              <CobuText variant="caption">
                {strings.buttons.hideReplies}
              </CobuText>
            </CobuStack>
          </CobuView>
        )}
      </View>
    </View>
  );
});

CommentReplies.displayName = 'CommentReplies';

export default CommentReplies; 